import { z } from 'zod';

// The card-on-trial signup on the v2 /start ad landings (SignUpLandingShell).
// Naming: domain.subject_verb, past tense — events describe what HAPPENED.
//
// This is the FUNNEL the CAC goal is measured on: every event carries `landing`
// (the ad angle — 'pco', 'cracks', 'welcome-aboard', …) so conversion can be
// broken down per door. `signup_trial.card_captured` is the day-0 leading
// indicator (a card is on file); `signup_trial.trial_started` is the confirmed
// trial. The real conversion (day-21 first charge) is a server event
// (ladder_stage 'paying'), not on the client.
//
// Every failure branch is a closed enum below and MUST emit — ADR 0002's
// motivating incident was the WA checkout shipping silent failure paths (zero
// purchases for days before anyone could see why). Adding a fail path to the
// shell without adding it here is a type error, which is the point.
// The step-1 gate reasons (`email_invalid`, `verification_incomplete`) were
// added 2026-08-02 after a nine-day zero-signup stretch: `goToStep2` returned
// silently on both, so 153 people reached the form, 19 entered an email, and
// the 134 who bounced at the gate were indistinguishable from people who never
// tried. A funnel step you can only measure at its exit is not instrumented.
export const SIGNUP_TRIAL_FAILURE_REASONS = [
  // The browser cancelled the submit before our handler ran (`type="email"`,
  // `required`). Distinct from `email_invalid` on purpose: the visitor saw a
  // native bubble, not our inline copy, so it's a different fix.
  // `message` carries the offending field.
  'form_blocked_natively',
  'email_invalid',
  'verification_incomplete',
  // The Turnstile widget is dead with no path to a token in THIS browser —
  // an in-app webview block, or a browser whose offered retry also failed.
  // The form swaps its submit for the open-in-browser escape at this moment;
  // this emit is what ties that dead end into the funnel by `landing`
  // (`message` carries which of the two shapes it was). The turnstile.*
  // events carry the mechanics (code, attempt, stall window); this carries
  // the funnel loss.
  'turnstile_dead_end',
  'stripe_not_ready',
  'plan_not_selected',
  'begin_trial_failed',
  'card_declined',
  'account_exists',
  'onboarding_failed',
  'finalize_failed',
  // completeOnboarding succeeded (the church exists) but finalizeTrial never
  // confirmed — a decline-proof card is saved, yet Church.customer stays NULL
  // and a retry dead-ends on "account already exists, sign in" with no
  // password. Added 2026-09-25 after that gap sat unmonitored for 63 days.
  // `churchId` (below) is what makes this reason actionable: without it,
  // "an unknown church is orphaned" isn't a followable lead.
  'subscription_orphaned',
  // The Payment Element fired its load error — the card field never rendered
  // (blocked script, network, bad client secret). `message` carries Stripe's
  // text. Added 2026-09-30 after 58 of 75 Sept card-step non-trialists left
  // with no failure event of any kind.
  'stripe_element_load_failed',
  // The captureLead call on Continue threw (network, 5xx, GraphQL error). The
  // person still advances to the card page by design, so without this the lead
  // is lost with no trace; a retry only happens if they press Continue again.
  // `message` carries the error text. Added 2026-09-30.
  'lead_capture_failed',
  // confirmSetup refused BEFORE reaching the bank: incomplete number, bad
  // expiry, missing field. A different fix from `card_declined` (input UX, not
  // the issuing bank), so it gets its own reason. `code` carries Stripe's.
  'card_validation_error',
] as const;

export type SignupTrialFailureReason =
  (typeof SIGNUP_TRIAL_FAILURE_REASONS)[number];

// The SERVER side of the same funnel. The browser's `signup_trial.failed`
// only fires if the browser is still there to hear the error, and the cases
// that matter most on this path (a double tap, a retry after a lost response)
// are exactly the ones where it may not be. These are emitted by the tRPC
// procedures themselves, one per failure branch.
const SIGNUP_TRIAL_SERVER_FAILURE_REASONS = [
  // beginTrial: Stripe refused or errored creating/reusing the customer or
  // SetupIntent. `message` carries Stripe's text.
  'setup_intent_failed',
  'setup_intent_no_client_secret',
  // finalizeTrial branches, in the order the procedure checks them.
  'card_not_confirmed',
  'billing_conflict',
  'plan_missing',
  // Price resolution or the trialing subscription create. An idempotency
  // conflict that outlasted the retry budget lands here with Stripe's
  // "another in-progress request" text in `message`.
  'subscription_failed',
] as const;

export type SignupTrialServerFailureReason =
  (typeof SIGNUP_TRIAL_SERVER_FAILURE_REASONS)[number];

// `landing` — the ad angle that drove the signup, for CAC-by-door. Present on
// every event; optional in the schema so a missing path never blocks an emit.
const landing = z.string().optional();

// Device truth taken AT the card step, carried on every card-step event so a
// slice by phone vs desktop never depends on the first landing pageview
// (2026-09-30). Optional so a missing path never blocks an emit.
const device = z.enum(['mobile', 'tablet', 'desktop']).optional();
const inAppBrowser = z.boolean().optional();
const cardCtx = { device, inAppBrowser, landing };

// --- Trial start attempt (ADR 0015) ---------------------------------------
//
// One guest's try at reaching a Trial start is ONE `attemptId`, minted in the
// browser at step 1 and carried by every event below from BOTH sides. `side`
// says who emitted it: the server's events are the source of truth for
// OUTCOMES (church exists, subscription exists), the browser's for what the
// guest EXPERIENCED (the card form, the spinner, the error copy). Reading
// `trial_start.failed` where side=server against side=browser for the same
// attemptId is how a failure the guest never saw (closed tab) or never caused
// (server-side) becomes visible. Server events go to the catalog ONLY, never to
// an ad platform: ad-platform sends stay where they were so pixel counts do not
// double (server Meta Lead once ran 2x the browser count).
export const TRIAL_START_FAILURE_REASONS = [
  // Browser, before the server is asked.
  'stripe_not_ready',
  'plan_not_selected',
  'card_declined',
  'card_validation_error',
  // The request never produced a usable answer (network, 5xx, parse).
  'request_failed',
  // Server step.
  'feature_off',
  'card_not_confirmed',
  'account_exists',
  'church_creation_failed',
  'billing_conflict',
  'plan_missing',
  'subscription_failed',
  // The subscription exists but the church link did not land. The one reason
  // that names money already moved.
  'subscription_orphaned',
  // Browser-sequenced path (flag OFF), same taxonomy so the two compare.
  'onboarding_failed',
  'finalize_failed',
] as const;

export type TrialStartFailureReason =
  (typeof TRIAL_START_FAILURE_REASONS)[number];

const attemptCtx = {
  attemptId: z.string(),
  side: z.enum(['browser', 'server']),
};

const tierCtx = { tier: z.string().optional() };

/** Pre-catalog PostHog names kept alive for the five raw captures moved here. */
export const SIGNUP_TRIAL_LEGACY_POSTHOG_NAMES = {
  'signup_trial.account_signed_up': 'newSignUp',
  'signup_trial.church_registered': 'newChurch',
  'signup_trial.email_entered': 'emailEntered',
  'signup_trial.finish_failed': 'signup_trial_finish_failed',
  'signup_trial.lead_captured': 'leadCaptured',
} as const;

export const signupTrialSchemas = {
  /** The signup form was reached — the funnel entry the /start ads optimize on. */
  'signup_trial.started': z.object({ landing }),
  /** Card confirmed (SetupIntent) — the day-0 leading indicator, BEFORE any church. */
  'signup_trial.card_captured': z.object({
    device,
    inAppBrowser,
    landing,
    tier: z.string(),
  }),
  /** completeOnboarding provisioned the church + logged the buyer in. */
  'signup_trial.church_created': z.object({ device, inAppBrowser, landing }),
  /** finalizeTrial started the 21-day trialing subscription on the chosen tier. */
  'signup_trial.trial_started': z.object({
    device,
    inAppBrowser,
    landing,
    tier: z.string(),
  }),
  /**
   * Any failure branch of the flow. `reason` is the closed enum above.
   * `churchId` is present only for `subscription_orphaned` — the one reason
   * that names an account already sitting in the DB rather than a moment
   * nothing was created.
   */
  'signup_trial.failed': z.object({
    churchId: z.string().optional(),
    /** Stripe's error `code` (card_declined, incomplete_number…), when it gave one. */
    code: z.string().optional(),
    device,
    inAppBrowser,
    landing,
    message: z.string().optional(),
    reason: z.enum(SIGNUP_TRIAL_FAILURE_REASONS),
  }),
  /** A server-side failure branch of beginTrial / finalizeTrial. */
  'signup_trial.step_failed': z.object({
    /** Present on finalize (the church exists by then), absent on begin. */
    churchId: z.string().optional(),
    message: z.string().optional(),
    reason: z.enum(SIGNUP_TRIAL_SERVER_FAILURE_REASONS),
    step: z.enum(['begin', 'finalize']),
  }),

  // --- Engagement (below-the-fold reading), added 2026-07-28 ---------------
  //
  // The signup pages had NO engagement instrumentation: scroll_depth /
  // time_on_page / faq_opened fire only on the marketing app, so a query
  // against /sign-up returned zeros that read like "nobody engages" but were
  // really "nothing is measured". That blind spot blocked the actual question
  // behind the page's trust content — do people read far enough to reach the
  // pricing and founder-story sections, and do they open the FAQ?
  //
  // Keyed on `landing` (not pathname) so it slices the same way as the rest of
  // this funnel. The marketing app's equivalents stay pathname-keyed; compare
  // across the two by door, not by URL.
  /** A scroll-depth milestone (25/50/75/100) was crossed on a signup page. */
  'signup_trial.page_scrolled': z.object({
    landing,
    milestone: z.number(),
    /** Which of the two steps was on screen — reading differs sharply by step. */
    step: z.number().optional(),
  }),
  /** An FAQ item was expanded — a direct read on unresolved objections. */
  'signup_trial.faq_opened': z.object({ landing, question: z.string() }),
  /** An active-foreground dwell milestone (30s/60s/180s) was reached. */
  'signup_trial.dwelled': z.object({ landing, seconds: z.number() }),

  // --- Form engagement, added 2026-08-03 -----------------------------------
  //
  // Step 1 is where the funnel actually bleeds: over the Aug2026 campaign
  // window 141 people landed, 4 submitted an email, and NOBODY has reached the
  // card step. But every existing step-1 signal fires either on arrival
  // (`started`) or on a submit attempt (`failed`), so the ~137 who left in
  // between were all one undifferentiated blob — a visitor who read the offer
  // and rejected it looked identical to one who typed into the form and got
  // stuck behind a stalled Turnstile. Those point at opposite fixes (landing
  // copy vs. mechanics), and we could not tell them apart.
  //
  // Fires ONCE per page, on first real input into either step-1 field. Paired
  // with `started` it gives the honest denominator: landed → engaged → email.
  /** The visitor typed into a step-1 field for the first time. */
  'signup_trial.form_engaged': z.object({
    /** Which field they touched first — the two imply different hesitation. */
    field: z.enum(['firstName', 'email']),
    landing,
  }),

  // --- The card step became its own page, added 2026-08-04 -----------------
  //
  // While step 2 rendered inline, `signup_trial.started` covered it: one page,
  // one arrival. Moving the card ask to its own route breaks that — without an
  // arrival event of its own there is no way to tell "never reached the card
  // step" from "reached it and left", which is EXACTLY the blind spot
  // `form_engaged` and the step-1 gate reasons were added to close, reproduced
  // one step later. A funnel step you can only measure at its exit is not
  // instrumented.
  //
  // Pairs with `card_captured` (the next event on the happy path) to give the
  // card step an honest denominator, and with `failed` to attribute the exits.
  // --- The training door, added 2026-09-04 ---------------------------------
  //
  // /start/training is the first landing whose main ask is NOT the form: it
  // sends people into the free OnArk training class at /onark/training, with
  // the trial form as the "ready now" alternative. The marketing app's
  // `training_cta_clicked` (marketing/components/analytics) is dead on /start —
  // that module only initializes under app/(marketing)/layout — so a click
  // there would vanish and the lander → training rate could only be read off
  // check-ins, one page later. Keyed on `landing` like the rest of the funnel
  // so the two asks on one page can be compared by door.
  /** The visitor clicked into the free training class from a signup landing. */
  'signup_trial.training_cta_clicked': z.object({
    landing,
    /** Which placement on the page — hero, closing, sticky… */
    source: z.string(),
  }),

  /**
   * The visitor clicked the free-trial link on a landing whose main ask is the
   * training class (/start/training, secondary CTA since 2026-09-28). Same
   * shape as training_cta_clicked so the two asks on one page compare by door
   * and placement.
   */
  'signup_trial.trial_cta_clicked': z.object({
    landing,
    /** Which placement on the page — hero, closing… */
    source: z.string(),
  }),

  /** The card page rendered with a usable SetupIntent — the card ask is live. */
  'signup_trial.card_step_reached': z.object({
    landing,
    /**
     * How the visitor got here. `resumed` means they refreshed or came back to
     * the URL and we rehydrated from storage rather than arriving from step 1
     * — a population worth watching, because a page you can land on cold is a
     * page that can dead-end.
     */
    entry: z.enum(['step_one', 'resumed']),
    /**
     * Added 2026-09-30: mobile captured 20% of card-step arrivals vs 70% on
     * desktop, and nothing said whether that was the card wall or the phone.
     * Coarse viewport class (width < 768 mobile, < 1024 tablet), not a UA parse.
     */
    device,
    /** Facebook/Instagram/TikTok… webview — the paid-social traffic. */
    inAppBrowser,
    /** window.innerHeight at arrival: how much of the page a phone shows. */
    viewportHeight: z.number().optional(),
  }),

  // --- Inside the card step, added 2026-09-30 -------------------------------
  //
  // `card_step_reached` → `card_captured` was one opaque gap. These split it:
  // did the card field render, did anyone touch it, which method did they pick,
  // did they read the terms, and how long until they left. Metadata only; no
  // card data ever reaches an event.
  /** The Stripe Payment Element rendered and is ready for input. */
  'signup_trial.card_element_loaded': z.object({
    ...cardCtx,
    /** ms from card-step arrival to the element being ready. */
    loadMs: z.number().optional(),
  }),
  /** First focus into the Stripe card field (once per page view). */
  'signup_trial.card_field_focused': z.object({
    ...cardCtx,
    /** Seconds on the step when it happened. */
    seconds: z.number(),
  }),
  /**
   * The selected payment method in the Payment Element changed. `method` is
   * Stripe's type ('card', 'apple_pay', 'google_pay', 'link'…), so wallet use
   * on phones is readable. Once per distinct method per page view.
   */
  'signup_trial.payment_method_selected': z.object({
    ...cardCtx,
    method: z.string(),
  }),
  /**
   * The "what you're agreeing to" terms (price, charge date, cancel rule)
   * scrolled into view. Once per page view. Absent on a failed visitor means
   * they never saw the price terms before leaving.
   */
  'signup_trial.terms_viewed': z.object({
    ...cardCtx,
    /** True when the terms were already on screen when the page first laid out. */
    atMount: z.boolean(),
    seconds: z.number(),
    tier: z.string(),
  }),
  /**
   * Still here. Every ~15s while the card step is visible AND focused, capped
   * (20 beats = 5 min). Added 2026-09-30: only 25 of 78 card-step
   * non-capturers emitted any `$pageleave`, so an idle-then-close read as a
   * fast leaver. A visitor with heartbeats and no capture hesitated; one with
   * none left early. `activeSeconds` counts visible+focused time only.
   */
  'signup_trial.card_step_heartbeat': z.object({
    ...cardCtx,
    activeSeconds: z.number(),
    beat: z.number().int().positive(),
    /** document.hasFocus() at the beat, for filtering; NOT a send gate. */
    focused: z.boolean().optional(),
  }),
  /**
   * The visitor left or backgrounded the card step with no card captured.
   * Fires once, on the first `pagehide`, `visibilitychange`→hidden (not while a
   * submit is in flight) or unmount (client-side Back / link). A phone
   * visitor who switches to a wallet/banking app and comes back also fires
   * this, so read it against a later `card_captured` for the same person.
   * This is also the final dwell sample.
   */
  'signup_trial.card_step_abandoned': z.object({
    ...cardCtx,
    /** Visible+focused seconds, the honest dwell (`seconds` includes idle). */
    activeSeconds: z.number(),
    /** Whether they had touched the card field before leaving. */
    fieldFocused: z.boolean(),
    /** Seconds on the step. */
    seconds: z.number(),
    /** Whether they had scrolled the terms into view. */
    termsViewed: z.boolean(),
    via: z.enum(['pagehide', 'hidden', 'unmount']),
  }),

  // --- Trial start attempt events (ADR 0015), added 2026-10-16 -------------
  /** Browser: the guest pressed submit and confirmSetup is about to run. */
  'trial_start.card_confirm_started': z.object({
    ...attemptCtx,
    ...tierCtx,
    device,
    inAppBrowser,
    landing,
  }),
  /** Browser: Stripe confirmed the SetupIntent. */
  'trial_start.card_confirm_succeeded': z.object({
    ...attemptCtx,
    ...tierCtx,
    device,
    inAppBrowser,
    landing,
  }),
  /** Browser: confirmSetup refused. `code` is Stripe's. */
  'trial_start.card_confirm_failed': z.object({
    ...attemptCtx,
    ...tierCtx,
    code: z.string().optional(),
    device,
    inAppBrowser,
    landing,
    message: z.string().optional(),
    reason: z.enum(TRIAL_START_FAILURE_REASONS),
  }),
  /** Browser: the card is confirmed and the church + subscription are being requested. */
  'trial_start.finalize_requested': z.object({
    ...attemptCtx,
    ...tierCtx,
    device,
    inAppBrowser,
    landing,
    /** `start_trial` is the server step; `finalize_trial` the browser-sequenced path. */
    via: z.enum(['start_trial', 'finalize_trial']),
  }),
  /** Server: the start-trial step received an attempt. */
  'trial_start.received': z.object({
    ...attemptCtx,
    ...tierCtx,
  }),
  /** Server: the legacy API created the church and owner. */
  'trial_start.church_created': z.object({
    ...attemptCtx,
    churchId: z.string(),
  }),
  /** Server: the trialing subscription exists on the chosen tier. */
  'trial_start.subscription_created': z.object({
    ...attemptCtx,
    ...tierCtx,
    churchId: z.string(),
  }),
  /** Server or browser: the Trial start happened (card confirmed, church and subscription exist). */
  'trial_start.completed': z.object({
    ...attemptCtx,
    ...tierCtx,
    churchId: z.string().optional(),
    /** Server only: the attempt was already complete, nothing was created again. */
    resumed: z.boolean().optional(),
  }),
  /** Server or browser: the attempt failed, with a classified reason. */
  'trial_start.failed': z.object({
    ...attemptCtx,
    ...tierCtx,
    churchId: z.string().optional(),
    code: z.string().optional(),
    message: z.string().optional(),
    reason: z.enum(TRIAL_START_FAILURE_REASONS),
  }),

  // --- The five raw posthog.capture calls in app/**, moved 2026-10-16 -------
  // Emitted under their original PostHog names (SIGNUP_TRIAL_LEGACY_POSTHOG_NAMES)
  // so every existing insight keeps working.
  /** Day-0 account signup counted (PostHog `newSignUp`). */
  'signup_trial.account_signed_up': z.object({}),
  /** A church registered (PostHog `newChurch`). */
  'signup_trial.church_registered': z.object({ churchName: z.string() }),
  /** First valid email entered (PostHog `emailEntered`). */
  'signup_trial.email_entered': z.object({}),
  /** The browser-side finish failed (PostHog `signup_trial_finish_failed`). */
  'signup_trial.finish_failed': z.object({
    message: z.string(),
    tier: z.string(),
  }),
  /** A Turnstile-verified lead (PostHog `leadCaptured`), with first-touch cell fields. */
  'signup_trial.lead_captured': z.record(z.string(), z.unknown()),
};
